import { z } from "zod";
export const resourceReferenceTypeSchema = z.enum(["artifact", "file", "session"]);
/**
 * Who, besides organisation admins, can open a session, artifact or file under
 * its read rules. Private channels the viewer cannot read are counted, never named.
 */
export const resourceAccessSchema = z.object({
    /** Every member of the organisation; guests are not members. */
    organisation: z.boolean(),
    /** Private channels whose members can open it, that the viewer can read. */
    channels: z.array(z.object({ id: z.string().min(1), name: z.string() }).strict()),
    /** Private channels whose members can open it, that the viewer cannot read. */
    otherChannels: z.number().int().nonnegative(),
    /** People who can open it themselves: its owner or uploader. */
    userIds: z.array(z.string().min(1)),
}).strict();
/** A linked or attached item that some readers of the channel it was placed in cannot open. */
export const channelUnopenableResourceSchema = z.object({
    type: resourceReferenceTypeSchema,
    id: z.string().min(1),
    title: z.string(),
    access: resourceAccessSchema,
}).strict();
/** Present when non-empty: placing or linking an item never widens who can open it. */
export const channelCannotOpenSchema = z.array(channelUnopenableResourceSchema).describe("Items placed or linked here that some of the channel's readers cannot open; their access is unchanged. Tell the user who is left out.");
/** An http(s) URL from a link, resolved against `origin` when it is relative. */
export function webLinkUrl(href, origin) {
    try {
        const url = new URL(href, origin);
        return ["http:", "https:"].includes(url.protocol) ? url : null;
    }
    catch {
        return null;
    }
}
/** The session, artifact or file a link to this workspace (at `origin`) opens, if any. */
export function internalLinkResource(href, origin) {
    const url = webLinkUrl(href, origin);
    if (!url || url.origin !== origin || url.username || url.password)
        return null;
    const artifact = url.searchParams.get("artifact");
    if (artifact && (url.pathname === "/knowledge" || /^\/workspace\/sessions\/[^/]+\/?$/.test(url.pathname))) {
        return { type: "artifact", id: artifact };
    }
    const file = url.pathname === "/knowledge" && url.searchParams.get("file");
    if (file)
        return { type: "file", id: file };
    const match = /^\/workspace\/(sessions|artifacts)\/([^/]+)\/?$/.exec(url.pathname);
    if (!match || match[2] === "new")
        return null;
    try {
        return { type: match[1] === "sessions" ? "session" : "artifact", id: decodeURIComponent(match[2]) };
    }
    catch {
        return null;
    }
}
export const resourceReferenceSchema = z.object({
    id: z.string().min(1),
    type: resourceReferenceTypeSchema,
    title: z.string(),
    kind: z.string().optional(),
    status: z.string().optional(),
    access: resourceAccessSchema.optional(),
}).strict();
export const resourceReferencesQuerySchema = z.object({
    type: resourceReferenceTypeSchema,
    q: z.string().trim().max(200).default(""),
    ids: z.array(z.string().trim().min(1).max(200)).min(1).max(100).optional(),
    cursor: z.string().trim().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
}).strict();
export const resourceReferencesResponseSchema = z.object({
    resources: z.array(resourceReferenceSchema),
    nextCursor: z.string().nullable(),
}).strict();
